/**
 * View-model for the live projection's ONE chart (round-2 W7.6 / R2-46, the
 * chart-customisation FEEDBACK batch, and the round-5 collapse below).
 *
 * ROUND 5 (feedback 2026-08-13): this used to hold a LIST of chart cards — a
 * net-worth card, a composition card, and one more card per asset group the
 * adviser drilled into, stacked down the page under a separate cash-flow chart.
 * The ask was "one chart at the top … then within that chart you can select all
 * the different options you want so you can overlay and you can focus". So the
 * state is now a SINGLE view: which dataset it plots, which series are shown on
 * it, and how. Adding a group no longer adds a card — it adds a line to the one
 * chart, which is what "overlay" means.
 *
 * Pure state + reducer so the whole interaction surface — dataset switching,
 * series overlay and focus, type switching, drag-zoom — is unit-tested without
 * a browser.
 *
 * Persistence (G4) is per-client localStorage. Deliberately NOT the fact-find
 * payload: `householdFactFindDocumentSchema` is the engine's projection request
 * minus per-run fields, so chart preferences stored there would leak UI state
 * into the engine contract. Cross-device persistence is parked as its own
 * contract decision.
 */

/**
 * Kept on the stored shape for compatibility with set-ups saved before the
 * Results redesign (2026-09-13 §11), which offered no mark choice. Carried,
 * never read: every set-up saved in that period holds the old default 'area',
 * which nobody chose — the adviser's real choice lives in `marks`.
 */
export type LiveChartType = 'area' | 'line' | 'bar';

/**
 * FEEDBACK 2026-09-18: "I should be able to select between lines, bar (bar
 * should be the default) and area for net worth, cashflow and inheritance
 * tax." One choice per chart, because the three draw different kinds of
 * quantity and the mark that suits one need not suit another.
 */
export type ChartMark = 'line' | 'bar' | 'area';
export const CHART_MARKS: readonly ChartMark[] = ['line', 'bar', 'area'];
export const CHART_MARK_LABELS: Readonly<Record<ChartMark, string>> = {
  line: 'Line',
  bar: 'Bar',
  area: 'Area',
};
export const DEFAULT_MARK: ChartMark = 'bar';

/**
 * WHICH SIDE OF THE YEAR THE CASH-FLOW COLUMN IS (FEEDBACK 2026-09-21: "one
 * bar for each year … stacked, and the sum of the stacks adds up to the scale
 * on the chart"). A year is ONE column, and the switch says whether that
 * column is money out (the default, tax at the bottom) or money in. Whichever
 * side is not the column is drawn as a line across it — the two are opposing
 * quantities and their sum is not a figure, so they are never stacked
 * together.
 */
export type CashFlowSide = 'out' | 'in';
export const CASH_FLOW_SIDES: readonly CashFlowSide[] = ['out', 'in'];
export const CASH_FLOW_SIDE_LABELS: Readonly<Record<CashFlowSide, string>> = {
  out: 'Money out',
  in: 'Money in',
};

/**
 * What the one chart is plotting. These are two different KINDS of quantity —
 * a balance you hold versus an amount that flows through a year — and they
 * cannot share a Y axis without making one of them unreadable, so they are a
 * switch inside the chart rather than series you can overlay on each other.
 * Inheritance tax is a third — the estate against its allowances — and has
 * been a view of its own since FEEDBACK 2026-09-18 (it sat inside Net worth).
 */
export type ChartDataset = 'wealth' | 'cashflow' | 'iht';

/** The net-worth series key, shown by default and never removed from the picker. */
export const NET_WORTH_KEY = 'netWorth';
/** Outstanding debt; offered only when the plan actually carries some. */
export const LIABILITIES_KEY = 'liabilities';

/**
 * FEEDBACK 2026-09-09: "if the net worth (sum of the parts) is 2 million then
 * don't stack the parts on top and then put 2 million as the figure on the
 * scale of the chart, it looks confusing and wrong".
 *
 * It was wrong. A STACK is an addition — every band claims to be a part of the
 * height it sits in — and neither of these two series is a part of the wealth
 * composition:
 *
 *  - `netWorth` is the composition's own TOTAL. Stacking a total on the parts
 *    it already counts doubles the plan: an axis reading £8m over a household
 *    worth £3.7m, which is what the screenshot showed.
 *  - `liabilities` is a DEDUCTION. It is subtracted to reach net worth, so
 *    adding it to the wrappers overstates the estate by the mortgage twice
 *    over — once for the debt, once for the asset it is secured on.
 *
 * So composition membership is a property of the series, not of the toggle.
 * `LiveChart` stacks only the parts and draws these two as lines ACROSS that
 * stack, where a total belongs: one shape, read at a glance, that cannot lie
 * about the height of the column beneath it.
 */
export const NON_COMPOSITION_KEYS: readonly string[] = [NET_WORTH_KEY, LIABILITIES_KEY];

/** True when `key` is a wrapper the plan actually holds — a band a stack may add. */
export function isCompositionPart(key: string): boolean {
  return !NON_COMPOSITION_KEYS.includes(key);
}

export interface ChartViewState {
  dataset: ChartDataset;
  type: LiveChartType;
  /**
   * Series keys drawn on the wealth chart, in the order the adviser turned
   * them on. Never empty — turning the last one off is a no-op, because an
   * axis with nothing on it answers no question.
   */
  shown: readonly string[];
  /**
   * Stack the shown series into a composition instead of overlaying them. Only
   * meaningful with more than one series; the old "asset mix" card in one flag.
   */
  stacked: boolean;
  /** Composition shown as % of the shown total rather than £ (stacked only). */
  percent: boolean;
  /** Inclusive [firstYear, lastYear] the view is zoomed to, or null for the whole plan. */
  zoom: readonly [number, number] | null;
  /**
   * How this client's chart shows the plan's events (workspace v2 §11.7);
   * null follows the adviser's "Chart events" setting.
   */
  eventDisplay: ChartEventDisplay | null;
  /** Line, bar or area — one choice per chart, bar until the adviser says otherwise. */
  marks: Readonly<Record<ChartDataset, ChartMark>>;
  /** Which side the cash-flow column stacks; the other side is a line across it. */
  cashFlowSide: CashFlowSide;
  /**
   * Cash-flow series the adviser has taken OFF the chart from its key, so one
   * series can be read on a scale of its own (CF-2). VIEW state, per browser
   * and per client — never an adviser preference, because it is a statement
   * about this chart this afternoon, not about how this adviser works.
   */
  hiddenCashFlow: readonly string[];
  /**
   * Wealth series the adviser took off the chart FROM ITS KEY, remembered
   * apart from `shown` (CF-2 follow-up B). The two are not the same thing: a
   * series can be off the chart because the adviser never switched the asset
   * breakdown on, and "Show all" must not read that as something it hid. Every
   * key here was in `shown` when the key removed it, and "Show all" puts back
   * exactly these.
   */
  hiddenWealth: readonly string[];
}

/** Labelled circles (rail and key), Circles only (rail and an Events button), or Hidden. */
export type ChartEventDisplay = 'labelled' | 'circles' | 'hidden';

/**
 * ONE phrase for "the adviser has taken things off this chart", so the card
 * head, the full-screen stage note, the note under the key and the tooltip all
 * say it the same way. Plain count, correct singular, no judgement in it.
 */
export function hiddenLabel(count: number): string {
  return `${count} item${count === 1 ? '' : 's'} hidden`;
}

/**
 * The chart every plan opens on (Results §11): CASH FLOW, whole plan, events
 * shown. A client whose adviser chose otherwise keeps that choice — the stored
 * view is loaded as saved.
 */
export function defaultView(): ChartViewState {
  return {
    dataset: 'cashflow',
    type: 'area',
    shown: [NET_WORTH_KEY],
    stacked: false,
    percent: false,
    zoom: null,
    eventDisplay: null,
    marks: { cashflow: DEFAULT_MARK, wealth: DEFAULT_MARK, iht: DEFAULT_MARK },
    cashFlowSide: 'out',
    hiddenCashFlow: [],
    hiddenWealth: [],
  };
}

export type ChartViewAction =
  | { kind: 'setDataset'; dataset: ChartDataset }
  | { kind: 'setType'; type: LiveChartType }
  | { kind: 'toggleSeries'; seriesKey: string }
  | { kind: 'onlySeries'; seriesKey: string }
  | { kind: 'showSeries'; seriesKeys: readonly string[] }
  | { kind: 'setZoom'; zoom: readonly [number, number] }
  | { kind: 'clearZoom' }
  | { kind: 'toggleStacked' }
  | { kind: 'togglePercent' }
  | { kind: 'setEventDisplay'; eventDisplay: ChartEventDisplay | null }
  | { kind: 'setMark'; dataset: ChartDataset; mark: ChartMark }
  | { kind: 'setCashFlowSide'; side: CashFlowSide }
  /**
   * `available` is every cash-flow series this plan and this mark can draw, so
   * the reducer can refuse the click that would empty the chart — an axis with
   * nothing on it answers no question, the same rule `toggleSeries` keeps for
   * the wealth chart.
   */
  | { kind: 'toggleCashFlowSeries'; seriesKey: string; available: readonly string[] }
  | { kind: 'showAllCashFlowSeries' }
  /**
   * Take a wealth series off the chart from its KEY, or put it back. Unlike
   * `toggleSeries` (the Asset breakdown control) this remembers that the key
   * is what removed it, so "Show all" restores that and nothing else. Net
   * worth is hideable this way too — the older "always drawn" rule made
   * isolating a band pointless, because the total line set the axis (browser
   * review 2026-09-21) — but never the last series showing.
   */
  | { kind: 'toggleWealthKeySeries'; seriesKey: string }
  | { kind: 'showAllWealthKeySeries' }
  | { kind: 'reset' };

export function chartViewReducer(state: ChartViewState, action: ChartViewAction): ChartViewState {
  switch (action.kind) {
    case 'setDataset':
      // Zoom is a statement about years and survives the switch; the series
      // selection cannot, because the two datasets share no series keys.
      return action.dataset === state.dataset
        ? state
        : {
            ...defaultView(),
            dataset: action.dataset,
            zoom: state.zoom,
            type: state.type,
            // How each chart is drawn is a choice about that chart, and a visit
            // to another view and back must not forget it.
            marks: state.marks,
            cashFlowSide: state.cashFlowSide,
            hiddenCashFlow: state.hiddenCashFlow,
          };
    case 'setType':
      return { ...state, type: action.type };
    case 'toggleSeries': {
      const on = state.shown.includes(action.seriesKey);
      // Refuse to empty the chart: the last series stays until another is on.
      if (on && state.shown.length === 1) return state;
      const shown = on
        ? state.shown.filter((k) => k !== action.seriesKey)
        : [...state.shown, action.seriesKey];
      // Switching a series ON from the breakdown control clears any memory of
      // the key having hidden it — the two controls must not disagree.
      const hiddenWealth = on
        ? state.hiddenWealth
        : state.hiddenWealth.filter((k) => k !== action.seriesKey);
      // Stacking one series is just an area chart with extra words on the
      // toolbar, so it drops away on its own.
      return {
        ...state,
        shown,
        hiddenWealth,
        ...(shown.length < 2 ? { stacked: false, percent: false } : {}),
      };
    }
    case 'onlySeries':
      // Focus: click a line or band on the plot to isolate it. Already alone
      // means the click was a way back out, so it restores the default set.
      return state.shown.length === 1 && state.shown[0] === action.seriesKey
        ? { ...state, shown: [NET_WORTH_KEY], stacked: false, percent: false }
        : { ...state, shown: [action.seriesKey], stacked: false, percent: false };
    case 'showSeries': {
      const shown = action.seriesKeys.length === 0 ? [NET_WORTH_KEY] : [...action.seriesKeys];
      return { ...state, shown, ...(shown.length < 2 ? { stacked: false, percent: false } : {}) };
    }
    case 'setZoom': {
      const [a, b] = action.zoom;
      const zoom: readonly [number, number] = a <= b ? [a, b] : [b, a];
      // A one-year "range" is a slip of the hand, not a zoom.
      if (zoom[1] - zoom[0] < 1) return state;
      return { ...state, zoom };
    }
    case 'clearZoom':
      return { ...state, zoom: null };
    case 'toggleStacked': {
      if (state.shown.length < 2) return state;
      const stacked = !state.stacked;
      return { ...state, stacked, ...(stacked ? {} : { percent: false }) };
    }
    case 'togglePercent':
      return state.stacked ? { ...state, percent: !state.percent } : state;
    case 'setEventDisplay':
      return { ...state, eventDisplay: action.eventDisplay };
    case 'setMark':
      return state.marks[action.dataset] === action.mark
        ? state
        : { ...state, marks: { ...state.marks, [action.dataset]: action.mark } };
    case 'setCashFlowSide':
      return state.cashFlowSide === action.side ? state : { ...state, cashFlowSide: action.side };
    case 'toggleCashFlowSeries': {
      const hidden = state.hiddenCashFlow.includes(action.seriesKey)
        ? state.hiddenCashFlow.filter((k) => k !== action.seriesKey)
        : [...state.hiddenCashFlow, action.seriesKey];
      // Refuse to empty the chart: the last series showing stays on.
      if (action.available.length > 0 && action.available.every((k) => hidden.includes(k)))
        return state;
      return { ...state, hiddenCashFlow: hidden };
    }
    case 'showAllCashFlowSeries':
      return state.hiddenCashFlow.length === 0 ? state : { ...state, hiddenCashFlow: [] };
    case 'toggleWealthKeySeries': {
      const on = state.shown.includes(action.seriesKey);
      if (on) {
        // Refuse to empty the chart: the last series showing stays on, which
        // is what lets net worth itself be hidden safely.
        if (state.shown.length === 1) return state;
        return {
          ...state,
          shown: state.shown.filter((k) => k !== action.seriesKey),
          hiddenWealth: [...state.hiddenWealth, action.seriesKey],
        };
      }
      return {
        ...state,
        shown: [...state.shown, action.seriesKey],
        hiddenWealth: state.hiddenWealth.filter((k) => k !== action.seriesKey),
      };
    }
    case 'showAllWealthKeySeries': {
      if (state.hiddenWealth.length === 0) return state;
      const back = state.hiddenWealth.filter((k) => !state.shown.includes(k));
      return { ...state, shown: [...state.shown, ...back], hiddenWealth: [] };
    }
    case 'reset':
      return defaultView();
  }
}

// --- derived rows --------------------------------------------------------------

/**
 * Percent view rows: each SHOWN series as a share of the shown total that year.
 * Series the adviser turned off are excluded from the divisor as well as the
 * plot — otherwise turning Property off would leave the remaining bands topping
 * out at 60% with nothing on screen saying what the missing 40% was.
 */
export function percentRows<Row extends { year: number }>(
  rows: readonly (Row & Record<string, number>)[],
  shownKeys: readonly string[],
): ({ year: number } & Record<string, number>)[] {
  return rows.map((r) => {
    const total = shownKeys.reduce((t, k) => t + (r[k] ?? 0), 0);
    const out: { year: number } & Record<string, number> = { year: r.year };
    for (const k of shownKeys) out[k] = total > 0 ? ((r[k] ?? 0) / total) * 100 : 0;
    return out;
  });
}

// --- persistence ---------------------------------------------------------------

/**
 * v2 because round 5 changed the stored SHAPE from a list of cards to one view.
 * A v1 entry is not migrated field-by-field — the two shapes disagree about
 * what a saved set-up even is (three cards versus one chart with three series
 * on it), so guessing would put an adviser back in front of a chart nobody
 * chose. It is deleted on sight and the defaults are used, which is the same
 * outcome as the "unreadable state" path this loader has always had.
 */
const STORAGE_PREFIX = 'enzo.chartView.v2.';
const LEGACY_PREFIXES = ['pharos.chartViews.v1.'];

/**
 * A stored v2 entry, read leniently: `dataset`, `shown` and `zoom` must be
 * sound (they decide what is drawn), while the fields the Results redesign
 * stopped offering (`type`, `stacked`, `percent`) and the one it added
 * (`events`) take the default when absent or malformed. A set-up saved last
 * week therefore still opens on the chart the adviser left, and an entry
 * written by a newer build is never a reason to crash.
 */
/**
 * The stored event display. An entry saved before it existed kept a "Show
 * events" boolean: switched off stays off (Hidden); on follows the adviser.
 */
function readEventDisplay(v: Record<string, unknown>): ChartEventDisplay | null {
  const stored = v['eventDisplay'];
  if (stored === 'labelled' || stored === 'circles' || stored === 'hidden') return stored;
  return v['events'] === false ? 'hidden' : null;
}

/** Absent before FEEDBACK 2026-09-18; an unknown mark reads as the default, never a refusal. */
function readMarks(
  value: unknown,
  base: Readonly<Record<ChartDataset, ChartMark>>,
): Readonly<Record<ChartDataset, ChartMark>> {
  if (typeof value !== 'object' || value === null) return base;
  const m = value as Record<string, unknown>;
  const one = (k: ChartDataset): ChartMark => {
    const mark = m[k];
    return mark === 'line' || mark === 'bar' || mark === 'area' ? mark : base[k];
  };
  return { cashflow: one('cashflow'), wealth: one('wealth'), iht: one('iht') };
}

/**
 * Every series key the cash-flow chart knows how to hide: the six row fields
 * it can draw, plus the one total-money-out line. Anything else in a stored
 * set-up is a key from another build, a renamed series, or junk — dropped
 * rather than carried, so "N items hidden" can never count something the
 * chart has no way to show again.
 */
const CASH_FLOW_HIDEABLE: readonly string[] = [
  'income',
  'withdrawals',
  'tax',
  'spending',
  'liabilityPayments',
  'goalFunding',
  'moneyOut',
];

/** A stored list of series keys: strings only, de-duplicated, else empty. */
function readStringList(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((k): k is string => typeof k === 'string'))];
}

function readHiddenCashFlow(value: unknown): readonly string[] {
  return readStringList(value).filter((k) => CASH_FLOW_HIDEABLE.includes(k));
}

function readView(value: unknown): ChartViewState | null {
  if (typeof value !== 'object' || value === null) return null;
  const v = value as Record<string, unknown>;
  const stored = v['dataset'];
  if (stored !== 'wealth' && stored !== 'cashflow' && stored !== 'iht') return null;
  // A set-up saved while inheritance tax was a mode INSIDE Net worth (IE-4)
  // opens on the Inheritance tax view it was left on.
  const dataset: ChartDataset = stored === 'wealth' && v['wealthMode'] === 'iht' ? 'iht' : stored;
  const shown = v['shown'];
  if (!Array.isArray(shown) || shown.length === 0) return null;
  if (!shown.every((k) => typeof k === 'string')) return null;
  // Zoom must be a genuine, ordered, at-least-two-year span: `typeof NaN ===
  // 'number'`, so a crafted entry would otherwise filter every row out and
  // draw an empty plot instead of falling back to the defaults.
  const zoom = v['zoom'];
  const zoomOk =
    zoom === null ||
    (Array.isArray(zoom) &&
      zoom.length === 2 &&
      zoom.every((n) => Number.isFinite(n)) &&
      (zoom[1] as number) - (zoom[0] as number) >= 1);
  if (!zoomOk) return null;
  const base = defaultView();
  const type = v['type'];
  return {
    dataset,
    type: type === 'area' || type === 'line' || type === 'bar' ? type : base.type,
    shown: [...(shown as string[])],
    stacked: typeof v['stacked'] === 'boolean' ? v['stacked'] : base.stacked,
    percent: typeof v['percent'] === 'boolean' ? v['percent'] : base.percent,
    zoom: zoom === null ? null : [zoom[0] as number, zoom[1] as number],
    eventDisplay: readEventDisplay(v),
    marks: readMarks(v['marks'], base.marks),
    /**
     * A set-up saved between 2026-09-18 and CF-1 holds `incomeAs` instead.
     * Both of its values drew the money-OUT stack — 'bar' put money in beside
     * it, 'line' put income across it — so either reads as the money-out side
     * here, which is also the default. The old field is dropped rather than
     * migrated field-by-field: it described a two-column chart that no longer
     * exists, and an unknown value has never been a reason to refuse a set-up.
     */
    cashFlowSide: v['cashFlowSide'] === 'in' ? 'in' : base.cashFlowSide,
    hiddenCashFlow: readHiddenCashFlow(v['hiddenCashFlow']),
    hiddenWealth: readStringList(v['hiddenWealth']),
  };
}

/** Saved set-up for this client, or the defaults when absent or unreadable. */
export function loadChartView(clientId: string): ChartViewState {
  try {
    for (const legacy of LEGACY_PREFIXES) window.localStorage.removeItem(`${legacy}${clientId}`);
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${clientId}`);
    if (raw === null) return defaultView();
    const parsed: unknown = JSON.parse(raw);
    return readView(parsed) ?? defaultView();
  } catch {
    return defaultView();
  }
}

export function saveChartView(clientId: string, state: ChartViewState): void {
  try {
    // An untouched set-up stores nothing (and clears any stale entry): merely
    // opening a client must not accumulate a defaults row per client forever.
    if (JSON.stringify(state) === JSON.stringify(defaultView())) {
      window.localStorage.removeItem(`${STORAGE_PREFIX}${clientId}`);
      return;
    }
    window.localStorage.setItem(`${STORAGE_PREFIX}${clientId}`, JSON.stringify(state));
  } catch {
    // Storage full or blocked — the set-up simply lives for the session.
  }
}

/**
 * Remove every stored chart set-up, for sign-out: layout preferences carry no
 * client data, but a client UUID in the key has no business outliving the
 * session on a shared machine. Legacy prefixes are swept too, so a v1 entry
 * written before round 5 cannot survive a sign-out.
 */
export function clearAllChartViews(): void {
  try {
    const prefixes = [STORAGE_PREFIX, ...LEGACY_PREFIXES];
    const doomed: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key !== null && prefixes.some((p) => key.startsWith(p))) doomed.push(key);
    }
    for (const key of doomed) window.localStorage.removeItem(key);
  } catch {
    // Storage blocked — nothing stored, nothing to clear.
  }
}
